import { Component, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";

export interface StatusAlertConfig {
  title: string;
  subtitle?: string;
  type: "info" | "success" | "warning" | "error";
  mode?: "inline" | "block"; // inline = horizontal banner, block = centered empty state
  icon?: string;
}

@Component({
  selector: "app-status-alert",
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule],
  template: `
    <div class="status-alert" [ngClass]="[config.type, config.mode || 'inline']">
      <div class="alert-content">
        <div class="icon-wrapper">
          <mat-icon>{{ config.icon || getDefaultIcon() }}</mat-icon>
        </div>
        <div class="text-wrapper">
          <h3 class="title">{{ config.title | translate }}</h3>
          @if (config.subtitle) {
            <p class="subtitle">{{ config.subtitle | translate }}</p>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    .status-alert {
      display: flex;
      align-items: center;
      padding: 1.5rem;
      border-radius: 12px;
      margin: 1rem 0;
      transition: all 0.3s ease;
      
      &.inline {
        width: 100%;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
      }

      &.block {
        flex-direction: column;
        justify-content: center;
        text-align: center;
        padding: 3rem 2rem;
        background: transparent;
        border: 2px dashed rgba(0, 0, 0, 0.05);
        
        .alert-content {
          flex-direction: column;
          gap: 1.5rem;
        }

        .icon-wrapper {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          
          mat-icon {
            font-size: 40px;
            width: 40px;
            height: 40px;
          }
        }
      }

      .alert-content {
        display: flex;
        align-items: center;
        gap: 1.25rem;
        width: 100%;
      }

      .icon-wrapper {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        
        mat-icon {
          font-size: 24px;
        }
      }

      .text-wrapper {
        flex-grow: 1;
        
        .title {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          color: #1a202c;
        }

        .subtitle {
          margin: 0.25rem 0 0;
          font-size: 0.875rem;
          color: #4a5568;
          line-height: 1.5;
        }
      }

      // Themes
      &.info {
        background-color: #f7fafc;
        border-left: 4px solid #4299e1;
        .icon-wrapper { color: #4299e1; }
        &.block { 
          background: linear-gradient(145deg, #f7fafc 0%, #edf2f7 100%);
          border: none;
          .icon-wrapper { background-color: rgba(66, 153, 225, 0.1); }
        }
      }

      &.success {
        background-color: #f0fff4;
        border-left: 4px solid #48bb78;
        .icon-wrapper { color: #48bb78; }
        &.block { 
          background: linear-gradient(145deg, #f0fff4 0%, #c6f6d5 100%);
          border: none;
          .icon-wrapper { background-color: rgba(72, 187, 120, 0.1); }
        }
      }

      &.warning {
        background-color: #fffaf0;
        border-left: 4px solid #ed8936;
        .icon-wrapper { color: #ed8936; }
        &.block { 
          background: linear-gradient(145deg, #fffaf0 0%, #feebc8 100%);
          border: none;
          .icon-wrapper { background-color: rgba(237, 137, 54, 0.1); }
        }
      }

      &.error {
        background-color: #fff5f5;
        border-left: 4px solid #f56565;
        .icon-wrapper { color: #f56565; }
        &.block { 
          background: linear-gradient(145deg, #fff5f5 0%, #fed7d7 100%);
          border: none;
          .icon-wrapper { background-color: rgba(245, 101, 101, 0.1); }
        }
      }
    }
  `]
})
export class StatusAlertComponent {
  @Input({ required: true }) config!: StatusAlertConfig;

  getDefaultIcon(): string {
    switch (this.config.type) {
      case "success": return "check_circle";
      case "warning": return "warning";
      case "error": return "error";
      default: return "info";
    }
  }
}
